import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import confetti from "canvas-confetti";

const MAX_POLLS = 10;
const POLL_INTERVAL_MS = 2000;
const SUCCESS_CONFETTI = { particleCount: 120, spread: 100, origin: { y: 0.6 } };

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [state, setState] = useState("checking"); // checking | success | timeout | error
  const isPaypal = params.get("provider") === "paypal";
  const sessionId = params.get("session_id");
  const subscriptionId = params.get("subscription_id");
  const trackingId = isPaypal ? subscriptionId : sessionId;

  useEffect(() => {
    if (!trackingId) {
      setState("error");
      return;
    }
    let polls = 0;
    let active = true;

    const statusUrl = isPaypal
      ? `/paypal/status/${trackingId}`
      : `/payments/status/${trackingId}`;

    const poll = async () => {
      if (!active) return;
      try {
        const { data } = await api.get(statusUrl);
        if (data.payment_status === "paid") {
          await refreshUser();
          setState("success");
          confetti(SUCCESS_CONFETTI);
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
      } catch (e) {
        console.error("Payment status poll failed, will retry:", e);
        // keep polling
      }
      polls += 1;
      if (polls >= MAX_POLLS) {
        setState("timeout");
        return;
      }
      setTimeout(poll, POLL_INTERVAL_MS);
    };
    poll();
    return () => {
      active = false;
    };
  }, [trackingId, isPaypal, refreshUser]);

  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <div className="bg-white rounded-3xl p-10 shadow-sm border border-black/5 max-w-md w-full text-center animate-pop-in">
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 mx-auto animate-spin text-[#E8A87C]" />
            <h1 className="font-head font-extrabold text-2xl mt-5">Confirming your payment…</h1>
            <p className="text-muted-foreground mt-2">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "success" && (
          <>
            <div className="w-16 h-16 rounded-3xl mx-auto bg-[#48BB78] flex items-center justify-center">
              <CheckCircle2 className="w-9 h-9 text-white" />
            </div>
            <h1 className="font-head font-extrabold text-2xl mt-5">You're all set! 🎉</h1>
            <p className="text-muted-foreground mt-2">
              Your subscription is active. Enjoy unlimited access to every program.
            </p>
            <button
              onClick={() => navigate("/hub")}
              className="mt-7 bg-[#E8A87C] text-white font-semibold px-8 py-3.5 rounded-full hover:-translate-y-0.5 transition-transform"
              data-testid="success-continue"
            >
              Continue to programs
            </button>
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <div className="w-16 h-16 rounded-3xl mx-auto bg-[#F2EFEA] flex items-center justify-center">
              <XCircle className="w-9 h-9 text-[#718096]" />
            </div>
            <h1 className="font-head font-extrabold text-2xl mt-5">
              {state === "timeout" ? "Still processing" : "Something went wrong"}
            </h1>
            <p className="text-muted-foreground mt-2">
              {state === "timeout"
                ? "Your payment is taking longer than expected. It may still complete — check back shortly."
                : "We couldn't confirm this payment. Please try again."}
            </p>
            <button
              onClick={() => navigate("/subscribe")}
              className="mt-7 border border-black/10 font-semibold px-8 py-3.5 rounded-full hover:bg-black/5 transition-colors"
              data-testid="success-retry"
            >
              Back to subscription
            </button>
          </>
        )}
      </div>
    </div>
  );
}
